import { 
  Component, 
  OnInit, 
  ChangeDetectionStrategy,
  signal,
  computed,
  inject,
  effect,
  DestroyRef
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterModule, RouterOutlet } from '@angular/router';
import { AngularMaterialModule } from '../../angular-material.module';
import { CommonModule } from '@angular/common';
import { TemplateComponent } from '../../common/template/template.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { RaiseRequestsComponent } from '../../Dialogs/Dashboards/Requests/raise-requests/raise-requests.component';
import { AdminDashboardComponent } from '../admin-dashboard/admin-dashboard.component';
import { ApiService } from '../../services/api.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, finalize, retry, shareReplay } from 'rxjs/operators';
import { of, EMPTY } from 'rxjs';
import { canAccessDashboard, getDefaultLandingRoute } from '../../core/auth/role-navigation';

// Type definitions for better type safety
interface DashboardItem {
  readonly id: string;
  readonly title: string;
  readonly icon: string;
  readonly description: string;
  readonly route: readonly string[];
}

interface UserProfile {
  account_name?: string;
  [key: string]: unknown;
}

type PanelType = 'Dashboard' | 'Request Dashboard';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    FormsModule,
    RouterModule,
    ReactiveFormsModule,
    AngularMaterialModule,
    RouterOutlet,
    CommonModule,
    TemplateComponent,
    AdminDashboardComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss'
})
export class DashboardComponent implements OnInit {
  // Dependency injection using inject() function (Angular 17+)
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly apiService = inject(ApiService);
  private readonly destroyRef = inject(DestroyRef);

  // Session storage values (read-only, computed from sessionStorage)
  readonly roleID = signal<string | null>(this.getSessionItem('role_id'));
  readonly roleName = signal<string | null>(this.getSessionItem('role_name'));
  readonly userID = signal<string | null>(this.getSessionItem('session_id'));
  full_name = signal<string | null>(this.getSessionItem('full_name'));
  // Reactive state using signals
  readonly activePanel = signal<PanelType>('Dashboard');
  readonly userProfile = signal<UserProfile | null>(null);
  readonly isLoading = signal<boolean>(false);
  readonly error = signal<string | null>(null);

  // Computed signals for derived state
  readonly accountName = computed(() => {
    const fullName = this.full_name();
    if (!fullName) return 'User';
    // Extract first name (first word before space)
    return fullName.split(' ')[0] || 'User';
  });

  readonly isRequestDashboardActive = computed(() => 
    this.activePanel() === 'Request Dashboard'
  );

  // Ripple color for Material Design ripple effect
  readonly rippleColor = '#186391';

  // Immutable dashboard items configuration
  readonly dashboardItems: readonly DashboardItem[] = [
    {
      id: 'projects',
      title: 'Projects',
      icon: 'work_outline',
      description: 'View and manage projects',
      route: ['/projects'] as const
    },
    {
      id: 'assign-projects',
      title: 'Assign Projects',
      icon: 'assignment_ind',
      description: 'Assign projects to members',
      route: ['/project-assign'] as const
    },
    {
      id: 'documents',
      title: 'Documents',
      icon: 'description',
      description: 'Manage documents',
      route: ['/add-documents'] as const
    },
    {
      id: 'requests',
      title: 'Requests',
      icon: 'priority_high',
      description: 'View all requests',
      route: ['/all-requests'] as const
    },
    {
      id: 'members',
      title: 'All Members',
      icon: 'groups',
      description: 'View society members',
      route: ['/society-members'] as const
    },
    {
      id: 'notice-board',
      title: 'Notice Board',
      icon: 'content_paste',
      description: 'View notices',
      route: ['/notice-board'] as const
    },
    {
      id: 'event',
      title: 'Event',
      icon: 'calendar_today',
      description: 'Manage events',
      route: ['/event'] as const
    }
  ] as const;

  constructor() {
    // Effect to handle user profile updates
    effect(() => {
      const profile = this.userProfile();
      if (profile) {
        // Any side effects when profile changes can go here
        // For example: analytics, logging, etc.
      }
    });
  }

  ngOnInit(): void {
    const roleId = this.roleID();
    if (!canAccessDashboard(roleId)) {
      this.router.navigate([getDefaultLandingRoute(roleId)]);
      return;
    }

    this.activePanel.set('Dashboard');
    const userId = this.userID();
    if (userId) {
      this.fetchUserProfile(userId);
    }
  }

  /**
   * TrackBy function for *ngFor optimization
   * Prevents unnecessary DOM re-renders when array items change
   */
  trackByItemId(_index: number, item: DashboardItem): string {
    return item.id;
  }

  /**
   * Select panel with type safety
   */
  selectPanel(panel: PanelType): void {
    this.activePanel.set(panel);
  }

  /**
   * Navigate to route with error handling
   */
  navigateToRoute(route: readonly string[]): void {
    if (route && route.length > 0) {
      this.router.navigate([...route]).catch((error) => {
        console.error('Navigation error:', error);
        this.snackBar.open(
          'Failed to navigate. Please try again.',
          'Close',
          { duration: 3000 }
        );
      });
    }
  }

  /**
   * Open raise requests dialog with optimized error handling
   */
  raiseRequests(): void {
    const dialogRef = this.dialog.open(RaiseRequestsComponent, {
      autoFocus: false,
      width: '760px',
      maxWidth: '95vw',
      panelClass: 'submit-ticket-dialog',
      disableClose: false,
    });

    dialogRef.afterClosed()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError((error) => {
          console.error('Dialog error:', error);
          return EMPTY;
        })
      )
      .subscribe((result: boolean | undefined) => {
        if (result) {
          // Handle successful result if needed
          // Could trigger a refresh or notification
        }
      });
  }

  /**
   * Fetch user profile with advanced RxJS patterns
   * Includes retry logic, error handling, and loading states
   */
  private fetchUserProfile(userId: string): void {
    this.isLoading.set(true);
    this.error.set(null);

    this.apiService.fetchUserProfile(userId)
      .pipe(
        // Retry failed requests up to 2 times
        retry({ count: 2, delay: 1000 }),
        // Share and replay the result to prevent duplicate requests
        shareReplay({ bufferSize: 1, refCount: true }),
        // Handle errors gracefully
        catchError((error) => {
          console.error('Error fetching user profile:', error);
          this.error.set('Failed to fetch user profile, please try again later.');
          this.snackBar.open(
            this.error() || 'Failed to fetch user profile, please try again later.',
            'Close',
            { duration: 3000 }
          );
          return of(null);
        }),
        // Update loading state
        finalize(() => this.isLoading.set(false)),
        // Use takeUntilDestroyed for automatic cleanup
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (response: UserProfile | null) => {
          if (response) {
            this.userProfile.set(response);
          }
        }
      });
  }

  /**
   * Safe session storage getter with null handling
   */
  private getSessionItem(key: string): string | null {
    if (typeof sessionStorage !== 'undefined') {
      try {
        return sessionStorage.getItem(key);
      } catch (error) {
        console.warn(`Error accessing sessionStorage for key "${key}":`, error);
        return null;
      }
    }
    return null;
  }
}
